import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Facebook, Car, Copy, ExternalLink, CheckCircle, 
  AlertCircle, Image, DollarSign, FileText, RefreshCw,
  Loader2, Download, AlertTriangle, ArrowDown, ArrowUp, CheckCircle2, CarFront
} from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminFBMarketplace = () => {
  const { getAuthHeader } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [posting, setPosting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedField, setCopiedField] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [priceChanges, setPriceChanges] = useState({ price_changes: [], count: 0 });
  const [markingUpdated, setMarkingUpdated] = useState(null);

  useEffect(() => {
    fetchVehicles();
    fetchPriceChanges();
    const priceInterval = setInterval(fetchPriceChanges, 60000);
    return () => clearInterval(priceInterval);
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles?include_offline=true`, {
        headers: getAuthHeader(),
      });
      // Filter to only show available vehicles
      const available = response.data.filter(v => 
        v.status === 'available' || v.status === 'new_arrival' || v.status === 'reserved'
      );
      setVehicles(available);
    } catch (error) {
      toast.error('Erreur lors du chargement des véhicules');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copié!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error('Erreur de copie');
    }
  };

  const generateFBTitle = (vehicle) => {
    return `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim || ''}`.trim();
  };

  const generateFBDescription = (vehicle) => {
    let desc = vehicle.description || '';
    
    if (!desc) {
      desc = `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim || ''}\n\n`;
      desc += `📍 Kilométrage: ${vehicle.mileage?.toLocaleString() || 'N/A'} km\n`;
      desc += `⚙️ Transmission: ${vehicle.transmission || 'Automatique'}\n`;
      desc += `⛽ Carburant: ${vehicle.fuel_type || 'Essence'}\n`;
      if (vehicle.exterior_color) desc += `🎨 Couleur: ${vehicle.exterior_color}\n`;
      desc += `\n✅ Financement 1re, 2e et 3e chance disponible!\n`;
      desc += `📞 Appelez-nous: 1-833-995-6477\n`;
      desc += `📍 1007 boul. René-Lévesque, Verdun\n`;
    }
    
    return desc;
  };

  const formatPrice = (vehicle) => {
    if (!vehicle.price || vehicle.price === 0) {
      return 'Contactez-nous';
    }
    return vehicle.price.toString();
  };

  const openFBMarketplace = () => {
    window.open('https://www.facebook.com/marketplace/create/vehicle', '_blank');
  };

  const launchFBAutoFill = async (vehicle) => {
    setPosting(true);
    
    const fbData = {
      title: generateFBTitle(vehicle),
      price: formatPrice(vehicle),
      description: generateFBDescription(vehicle),
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      mileage: vehicle.mileage,
      transmission: vehicle.transmission || 'automatic',
      fuel_type: vehicle.fuel_type || 'gasoline',
      exterior_color: vehicle.exterior_color || '',
      body_type: vehicle.body_type || 'sedan',
      images: vehicle.images || [],
      vin: vehicle.vin,
      stock_number: vehicle.stock_number,
    };
    
    try {
      // Store vehicle data on server so bookmarklet can access it cross-domain
      await axios.post(`${API}/fb-marketplace/pending`, fbData, { headers: getAuthHeader() });
    } catch (error) {
      console.error('Error storing pending vehicle:', error);
    }
    
    // Open FB Marketplace
    window.open('https://www.facebook.com/marketplace/create/vehicle', '_blank');
    
    toast.success(
      <div>
        <p className="font-semibold">Données prêtes!</p>
        <p className="text-sm">1. Attendez que FB charge</p>
        <p className="text-sm">2. Cliquez le bookmarklet "Auto-Fill FB"</p>
      </div>,
      { duration: 10000 }
    );
    
    setPosting(false);
  };

  const downloadAllPhotos = async (vehicle, e) => {
    if (e) e.stopPropagation();
    
    if (!vehicle.images || vehicle.images.length === 0) {
      toast.error('No photos available');
      return;
    }
    
    setDownloading(vehicle.id);
    toast.info(`Préparation de ${vehicle.images.length} photos...`);
    
    try {
      const response = await fetch(`${API}/vehicles/${vehicle.id}/download-photos`);
      
      if (!response.ok) {
        toast.error('No photos available');
        setDownloading(null);
        return;
      }
      
      // Download the ZIP
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${vehicle.stock_number || vehicle.id}_photos.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Photos téléchargées!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('No photos available');
    } finally {
      setDownloading(null);
    }
  };

  const fetchPriceChanges = async () => {
    try {
      const res = await axios.get(`${API}/fb-bot/price-changes`);
      setPriceChanges(res.data);
    } catch (error) {
      console.error('Error fetching price changes:', error);
    }
  };

  const handleMarkPriceUpdated = async (stock) => {
    try {
      setMarkingUpdated(stock);
      await axios.post(`${API}/fb-bot/mark-price-updated/${stock}`, {}, { headers: getAuthHeader() });
      fetchPriceChanges();
    } catch (error) {
      console.error('Error marking price updated:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setMarkingUpdated(null);
    }
  };

  const openFacebookListing = (fbPostId) => {
    if (fbPostId) {
      window.open(`https://www.facebook.com/marketplace/item/${fbPostId}`, '_blank');
    } else {
      window.open('https://www.facebook.com/marketplace/you/selling', '_blank');
    }
  };

  const filteredVehicles = vehicles.filter(v => {
    const search = searchTerm.toLowerCase();
    return (
      v.make?.toLowerCase().includes(search) ||
      v.model?.toLowerCase().includes(search) ||
      v.stock_number?.toLowerCase().includes(search) ||
      v.year?.toString().includes(search)
    );
  });

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <Facebook className="h-6 w-6 text-blue-600" />
              Facebook Marketplace
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Publiez vos véhicules sur Facebook Marketplace en un clic
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button onClick={fetchVehicles} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-1" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Chrome Extension Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="extension-instructions">
          <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
            <Download className="h-4 w-4" />
            Extension Chrome - Auto-Fill FB Marketplace
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <a 
                href={`${API}/fb-marketplace/extension`}
                download
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                Télécharger l'extension v2.0
              </a>
            </div>
            <details className="text-sm text-blue-700">
              <summary className="cursor-pointer font-medium hover:underline">
                Guide d'installation (une seule fois)
              </summary>
              <ol className="list-decimal list-inside mt-2 space-y-1 text-xs">
                <li>Téléchargez et décompressez le fichier ZIP</li>
                <li>Ouvrez Chrome et allez à <strong>chrome://extensions</strong></li>
                <li>Activez le <strong>Mode développeur</strong> (en haut à droite)</li>
                <li>Cliquez <strong>"Charger l'extension non empaquetée"</strong></li>
                <li>Sélectionnez le dossier <strong>entrepot-fb-extension</strong> décompressé</li>
                <li>Cliquez l'icône de l'extension → Entrez l'URL: <strong>{process.env.REACT_APP_BACKEND_URL}</strong></li>
                <li>Cliquez <strong>Sauvegarder</strong></li>
              </ol>
            </details>
            <p className="text-xs text-blue-600">
              Après: Sélectionnez un véhicule → Cliquez "Poster sur FB" → Sur Facebook, cliquez le bouton bleu "Auto-Fill Entrepot" en bas à droite
            </p>
          </div>
        </div>

        {/* Price Changes Alert Card */}
        {priceChanges.count > 0 && (
          <Card className="border-2 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300" data-testid="price-changes-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500">
                    <AlertTriangle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800">
                      Mises à jour de prix requises
                    </h3>
                    <p className="text-xs text-amber-600">
                      {priceChanges.count} véhicule{priceChanges.count > 1 ? 's' : ''} avec changement de prix sur Facebook
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {priceChanges.price_changes.map((vehicle) => (
                  <div 
                    key={vehicle.stock}
                    className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-200"
                  >
                    <div className="flex items-center gap-3">
                      {vehicle.thumbnail ? (
                        <img 
                          src={vehicle.thumbnail} 
                          alt={vehicle.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center">
                          <CarFront className="h-6 w-6 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-slate-800">{vehicle.title}</p>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-slate-500 line-through">
                            ${vehicle.posted_price?.toLocaleString()}
                          </span>
                          <span className="text-slate-400">→</span>
                          <span className={`font-semibold ${vehicle.price_difference < 0 ? 'text-green-600' : 'text-red-600'}`}>
                            ${vehicle.current_price?.toLocaleString()}
                          </span>
                          <span className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            vehicle.price_difference < 0 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {vehicle.price_difference < 0 ? (
                              <><ArrowDown className="h-3 w-3" /> ${Math.abs(vehicle.price_difference).toLocaleString()}</>
                            ) : (
                              <><ArrowUp className="h-3 w-3" /> ${vehicle.price_difference.toLocaleString()}</>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openFacebookListing(vehicle.fb_post_id)}
                        className="gap-1 text-xs"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Modifier sur FB
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleMarkPriceUpdated(vehicle.stock)}
                        disabled={markingUpdated === vehicle.stock}
                        className="gap-1 text-xs bg-amber-500 hover:bg-amber-600"
                      >
                        {markingUpdated === vehicle.stock ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-3 w-3" />
                        )}
                        Fait
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-amber-600 mt-3">
                Cliquez sur "Modifier sur FB" pour mettre à jour le prix sur Facebook, puis "Fait" pour confirmer.
              </p>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Vehicle List */}
          <div className="lg:col-span-1 bg-white border border-slate-200 rounded-lg">
            <div className="p-3 border-b border-slate-200">
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                </div>
              ) : filteredVehicles.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  Aucun véhicule trouvé
                </div>
              ) : (
                filteredVehicles.map((vehicle) => (
                  <div
                    key={vehicle.id}
                    onClick={() => setSelectedVehicle(vehicle)}
                    className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedVehicle?.id === vehicle.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-12 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                        {vehicle.images?.[0] ? (
                          <img 
                            src={vehicle.images[0]} 
                            alt={vehicle.model}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Car className="h-5 w-5 text-slate-300" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-800 truncate">
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </p>
                        <p className="text-xs text-slate-500">
                          #{vehicle.stock_number} • {vehicle.mileage?.toLocaleString()} km
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Download Photos Button */}
                        <button
                          onClick={(e) => downloadAllPhotos(vehicle, e)}
                          disabled={downloading === vehicle.id || !vehicle.images?.length}
                          className={`p-1.5 rounded-full transition-colors ${
                            vehicle.images?.length 
                              ? 'hover:bg-green-100 text-green-600' 
                              : 'text-slate-300 cursor-not-allowed'
                          }`}
                          title={`Télécharger ${vehicle.images?.length || 0} photos`}
                        >
                          {downloading === vehicle.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </button>
                        <div className="text-right">
                          <p className="font-semibold text-sm text-green-600">
                            {vehicle.price ? `$${vehicle.price.toLocaleString()}` : 'Appelez'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Vehicle Details & FB Preview */}
          <div className="lg:col-span-2">
            {selectedVehicle ? (
              <div className="bg-white border border-slate-200 rounded-lg">
                {/* Vehicle Header */}
                <div className="p-4 border-b border-slate-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-800">
                        {generateFBTitle(selectedVehicle)}
                      </h2>
                      <p className="text-sm text-slate-500">
                        Stock: {selectedVehicle.stock_number} • VIN: {selectedVehicle.vin}
                      </p>
                    </div>
                    <Button
                      onClick={() => launchFBAutoFill(selectedVehicle)}
                      disabled={posting}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {posting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Facebook className="h-4 w-4 mr-2" />
                      )}
                      Poster sur FB
                    </Button>
                  </div>
                </div>

                {/* FB Preview */}
                <div className="p-4 space-y-4">
                  {/* Images */}
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-2">
                        <Image className="h-3 w-3" />
                        Photos ({selectedVehicle.images?.length || 0})
                      </label>
                      {selectedVehicle.images?.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => downloadAllPhotos(selectedVehicle, e)}
                          disabled={downloading === selectedVehicle.id}
                          className="h-7 text-xs"
                        >
                          {downloading === selectedVehicle.id ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <Download className="h-3 w-3 mr-1" />
                          )}
                          Télécharger tout
                        </Button>
                      )}
                    </div>
                    <div className="mt-2 flex gap-2 overflow-x-auto pb-2">
                      {selectedVehicle.images?.slice(0, 10).map((img, idx) => (
                        <img
                          key={idx}
                          src={img}
                          alt={`Photo ${idx + 1}`}
                          className="w-24 h-18 object-cover rounded border border-slate-200 flex-shrink-0"
                        />
                      ))}
                      {(!selectedVehicle.images || selectedVehicle.images.length === 0) && (
                        <div className="w-24 h-18 bg-slate-100 rounded flex items-center justify-center">
                          <AlertCircle className="h-5 w-5 text-orange-400" />
                        </div>
                      )}
                    </div>
                    {selectedVehicle.images?.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        📸 Uploadez ces photos manuellement sur FB
                      </p>
                    )}
                  </div>

                  {/* Title */}
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Titre
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex-1 p-2 bg-slate-50 rounded border text-sm">
                        {generateFBTitle(selectedVehicle)}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(generateFBTitle(selectedVehicle), 'title')}
                      >
                        {copiedField === 'title' ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Price */}
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-2">
                      <DollarSign className="h-3 w-3" />
                      Prix
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex-1 p-2 bg-slate-50 rounded border text-sm font-semibold text-green-600">
                        ${formatPrice(selectedVehicle)}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(formatPrice(selectedVehicle), 'price')}
                      >
                        {copiedField === 'price' ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-2">
                      <FileText className="h-3 w-3" />
                      Description
                    </label>
                    <div className="mt-1 flex items-start gap-2">
                      <div className="flex-1 p-2 bg-slate-50 rounded border text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {generateFBDescription(selectedVehicle)}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(generateFBDescription(selectedVehicle), 'desc')}
                      >
                        {copiedField === 'desc' ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Vehicle Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-2 bg-slate-50 rounded">
                      <p className="text-xs text-slate-500">Année</p>
                      <p className="font-medium">{selectedVehicle.year}</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <p className="text-xs text-slate-500">Kilométrage</p>
                      <p className="font-medium">{selectedVehicle.mileage?.toLocaleString()} km</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <p className="text-xs text-slate-500">Transmission</p>
                      <p className="font-medium">{selectedVehicle.transmission || 'Auto'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded">
                      <p className="text-xs text-slate-500">Carburant</p>
                      <p className="font-medium">{selectedVehicle.fuel_type || 'Essence'}</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-4 border-t border-slate-200">
                    <Button
                      onClick={() => launchFBAutoFill(selectedVehicle)}
                      disabled={posting}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      {posting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Facebook className="h-4 w-4 mr-2" />
                      )}
                      Ouvrir FB & Préparer
                    </Button>
                    <Button
                      onClick={openFBMarketplace}
                      variant="outline"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      FB Marketplace
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
                <Car className="h-12 w-12 text-slate-200 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600">Sélectionnez un véhicule</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Cliquez sur un véhicule dans la liste pour voir les détails
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminFBMarketplace;
